import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Output,
  ViewChild
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { finalize, forkJoin } from 'rxjs';
import { RequisicionesCandidatoService } from 'src/app/apps/nodo/modules/seleccion/requisiciones/services/requisiciones-candidato.service';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ICandidateInfo } from '../../interfaces/ICandidatoInfo';
import { Checkbox } from 'primeng/checkbox';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { StackDirectives } from 'src/app/shared/directives/stack-directives';
import { CommonModule } from '@angular/common';
import { MaterialModule } from 'src/app/material.module';
import { NgSignaturePadOptions, SignaturePadComponent } from '@almothafar/angular-signature-pad';
import { IFinalizeProcessCandidate } from '../../interfaces/IFinaliceProcessCandidate';
import { da, de } from 'date-fns/locale';

@Component({
  selector: 'app-info-finalizar',
  imports: [ReactiveFormsModule, InputTextModule, StackDirectives, CommonModule, MaterialModule, SignaturePadComponent],
  templateUrl: './info-finalizar.html',
  styleUrl: './info-finalizar.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InfoFinalizar {
  private ac = inject(ActivatedRoute);
  private loader = inject(LoaderService);
  private toastr = inject(ToastrService);
  private requisitionCandidateService = inject(RequisicionesCandidatoService);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);
  private fb = inject(FormBuilder);

  idEncrypt: any;
  person: ICandidateInfo | null = null;
  @Output() validChange = new EventEmitter<boolean>();

  termsUrl = 'https://misionempresarial.com/wp-content/uploads/2023/02/Politica-de-tratamiento-de-datos-personales.pdf';

  @ViewChild('signatureWrapper') signatureWrapper!: ElementRef<HTMLDivElement>;
  @ViewChild('signature') signaturePad!: SignaturePadComponent;

  form = this.fb.group({
    generalConsent: [false, Validators.requiredTrue],
    digitalSignatureConsent: [false, Validators.requiredTrue],
    signature: ['', Validators.required]
  });

  signaturePadOptions: NgSignaturePadOptions = {
    minWidth: 1.5,
    maxWidth: 3.5,
    throttle: 16,
    velocityFilterWeight: 0.7,
    penColor: '#111',
    backgroundColor: 'rgba(255,255,255,1)',
    canvasWidth: 400,
    canvasHeight: 200 // Altura base; luego la puedes recalibrar si necesitas
  };

  ngAfterViewInit(): void {
    this.fitCanvasToParent();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.fitCanvasToParent();
  }

  ngOnInit() {
    this.ac.queryParams.subscribe((params) => {
      const rawId = params['q'];
      if (rawId) {
        this.idEncrypt = encodeURIComponent(rawId);
        this.loadData();
        this.form.markAllAsTouched();
      } else {
        this.router.navigate(['/login']);
      }

      this.form.statusChanges.subscribe((status) => {
        this.validChange.emit(status === 'VALID');
      });
    });
  }

  loadData() {
    forkJoin({
      person: this.requisitionCandidateService.getInfoCandidateById(this.idEncrypt)
    })
      .pipe(finalize(() => this.loader.hide()))
      .subscribe({
        next: ({ person }) => {
          this.person = person.data;
          this.cdr.markForCheck();

          this.validationToFinalize(this.person);
        },
        error: (e) => {
          // console.log('Error', e);
        }
      });
  }

  finaliceProcess() {
    this.loader.show('Finalizando proceso...');
    const payload: IFinalizeProcessCandidate = {
      idEncrypt: this.idEncrypt,
      firmaDigital: this.form.get('signature')!.value,
      IndAceptaTerminos: this.form.get('generalConsent')!.value!,
      IndAceptaPolitica: this.form.get('digitalSignatureConsent')!.value!
    };
    this.requisitionCandidateService
      .finalizeProcessCandidate(payload)
      .pipe(finalize(() => this.loader.hide()))
      .subscribe({
        next: (resp) => {
          this.loader.hide();
          this.toastr.success('El proceso de selección ha sido finalizado con éxito.', 'Proceso finalizado');
          this.router.navigate(['/info-candidatos/resume'], {
            queryParams: { status: '1' }
          });
        },
        error: (e) => {
          this.toastr.error('Ocurrió un error al finalizar el proceso de selección.', 'Error');
        }
      });
  }

  getErrorMessage(controlName: string, errorMessage: string): string | null {
    const control = this.form.get(controlName);
    if (control?.touched) {
      if (control.hasError('required')) {
        return errorMessage ?? 'Este campo es requerido';
      }
    }
    return null;
  }

  get nombreCompleto(): string {
    // Si dataCandidate o candidateInfo no existen, usa strings vacíos
    const pn = this.person?.candidateInfo?.primerNombre ?? '';
    const sn = this.person?.candidateInfo?.segundoNombre ?? '';
    const pa = this.person?.candidateInfo?.primerApellido ?? '';
    const sa = this.person?.candidateInfo?.segundoApellido ?? '';

    return (
      [pn, sn, pa, sa]
        .map((v) => v?.toString().trim())
        .filter((v) => !!v && v.length > 0)
        .join(' ') || '—'
    );
  }

  private fitCanvasToParent(): void {
    if (!this.signatureWrapper || !this.signaturePad) return;

    const parent = this.signatureWrapper.nativeElement;
    const cssWidth = parent.clientWidth;

    // Evita recalcular si el ancho no cambió de forma significativa
    const currentWidth = (this.signaturePad as any).options?.canvasWidth;
    if (currentWidth && Math.abs(currentWidth - cssWidth) < 4) return;

    // 1) BACKUP: si hay contenido, conservarlo como base64
    let backupDataUrl: string | null = null;
    const hadSignature = !this.signaturePad.isEmpty();
    if (hadSignature) {
      backupDataUrl = this.signaturePad.toDataURL('image/png'); // o 'image/jpeg'
    }

    // 2) Calcular nuevo tamaño con límites saludables
    const desiredCssWidth = Math.max(300, Math.min(cssWidth, 500));
    const desiredCssHeight = Math.round(desiredCssWidth * 0.3);

    // Alta definición (HiDPI)
    // const ratio = Math.max(window.devicePixelRatio || 1, 1);
    // const pixelWidth = Math.floor(desiredCssWidth * ratio);
    // const pixelHeight = Math.floor(desiredCssHeight * ratio);

    // 3) Redimensionar el canvas del componente (esto limpia el lienzo)
    this.signaturePad.set('canvasWidth', desiredCssWidth);
    this.signaturePad.set('canvasHeight', desiredCssHeight);

    // 4) Ajustar CSS para que se vea con el ancho disponible
    const canvasEl: HTMLCanvasElement | undefined = (this.signaturePad as any)?.canvas;
    if (canvasEl) {
      canvasEl.style.width = `${desiredCssWidth}px`;
      canvasEl.style.height = `${desiredCssHeight}px`;
    }

    // 5) Restaurar firma si había contenido
    if (backupDataUrl) {
      this.signaturePad.fromDataURL(backupDataUrl);
      this.form.get('signature')!.setValue(backupDataUrl);
    }
  }

  limpiar() {
    this.signaturePad.clear();
  }

  onSignatureEnd(): void {
    if (this.signaturePad.isEmpty()) {
      this.form.get('signature')!.setValue(null);
      this.form.get('signature')!.markAsTouched();
      this.form.get('signature')!.updateValueAndValidity();
      return;
    }
    // Guardar la firma como base64 PNG
    const dataUrl = this.signaturePad.toDataURL('image/png');
    this.form.get('signature')!.setValue(dataUrl);
    this.form.get('signature')!.markAsDirty();
    this.form.get('signature')!.updateValueAndValidity();
    this.cdr.markForCheck();
  }

  validationToFinalize(data: ICandidateInfo): void {
    const dataUrl = data.candidateInfo.firmaDigital;
    //Prospecto no se carga nada en los demas estados si
    if (data.requisitionCandidate.idEstado !== 1) {
      if (dataUrl) {
        this.signaturePad.fromDataURL(dataUrl);
        this.form.patchValue({
          digitalSignatureConsent: true,
          generalConsent: true,
          signature: dataUrl
        });
      }
    }

    this.cdr.markForCheck();
  }
}
